import React from 'react';
import { ChartNode } from 'react-flame-graph';
import { WithDefaultProps } from '../WithDefaultProps';
import Loader from './loader/Loader';
import Landing from './landing/Landing';
import BundleFlameGraph from './flamegraph/BundleFlameGraph';
import Footer from './footer/Footer';
import Toast from './toast/Toast';
import getDataFromFiles from '../lib/getDataFromFiles';
import reverseGraph from '../lib/reverseGraph';
import getModifiedDependencies from '../lib/getModifiedDependencies';
import getShortenFileName from '../lib/getShortenFileName';
import './App.css';
import { Graph, DisplayNode, ColorTree, SizeMap, LinkSet, NullableDepLink } from '../types';

type OwnProps = {
  url: string | null;
};

type State = {
  data?: {
    entryPoints: string[];
    graphData: DisplayNode;
    fileSizes: SizeMap;
    dependencyGraph: Graph;
    topLevelFolders: string[];
    directoryColors: ColorTree;
    reversedDependencyGraph: Graph;
  };
  originalDependencyGraph?: Graph;
  originalReversedDependencyGraph?: Graph;
  file?: File;
  focusedNode?: DisplayNode | ChartNode;
  focusedNodeId?: string;
  loadingMessage: string;
  loadingPercent: number;
  addedLinks: LinkSet;
  removedLinks: LinkSet;
  toastMessage?: string | null;
  toastTimer?: ReturnType<typeof setTimeout> | null;
};

const defaultProps = {
  url: null,
};

type Props = WithDefaultProps<OwnProps, typeof defaultProps>;

class BundleFlameChart extends React.Component<Props, State> {
  flameGraphRef: $TSFixMe;

  worker: Worker;

  state: State = {
    loadingMessage: 'Loading...',
    loadingPercent: 0,
    addedLinks: new Set([]),
    removedLinks: new Set([]),
  };

  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    this.focusNode = this.focusNode.bind(this);
    this.addLink = this.addLink.bind(this);
    this.removeLink = this.removeLink.bind(this);
    this.handleSetRawBundle = this.handleSetRawBundle.bind(this);
    this.flameGraphRef = React.createRef();
    this.worker = new Worker('../graphDataWorker.worker', { type: 'module' });

    this.worker.addEventListener('message', (event) => {
      const { data, loadingMessage, loadingPercent, error } = event.data;

      if (data) {
        const { dependencyGraph, reversedDependencyGraph } = data;

        this.setState({
          data,
          focusedNode: data.graphData,
          focusedNodeId: data.graphData.id,
          originalDependencyGraph: dependencyGraph,
          originalReversedDependencyGraph: reversedDependencyGraph,
        });
      }
      if (loadingMessage) {
        this.setState({
          loadingMessage,
          loadingPercent,
        });
      }
      if (error) {
        console.error('Error from worker:');
        console.error(error);
      }
    });
  }

  componentDidMount() {
    const { url } = this.props;
    if (!url) {
      return;
    }

    const { worker } = this;

    worker.postMessage({ url });
  }

  handleSetRawBundle(file: File) {
    const { worker } = this;

    worker.postMessage({ file });
    this.setState({ file });
  }

  toast(toastMessage: string) {
    const { toastTimer } = this.state;
    if (toastTimer) {
      clearTimeout(toastTimer);
    }

    const newToastTimer = setTimeout(() => {
      this.setState({
        toastMessage: null,
        toastTimer: null,
      });
    }, 4000);

    this.setState({
      toastMessage,
      toastTimer: newToastTimer,
    });
  }

  modifyDeps(addedLinks: LinkSet, removedLinks: LinkSet, reFocus = () => {}) {
    const { data, originalDependencyGraph } = this.state;

    if (!originalDependencyGraph || !data) return;

    const { entryPoints, fileSizes, topLevelFolders, directoryColors } = data;

    const dependencyGraph = getModifiedDependencies(
      originalDependencyGraph,
      addedLinks,
      removedLinks,
    );

    const reversedDependencyGraph = reverseGraph(dependencyGraph);
    const shortenFileName = getShortenFileName(topLevelFolders);

    // TODO: Move this back into the web worker to avoid bundle size bump
    //       or figure out how to do more targeted shims
    const graphData = getDataFromFiles(
      entryPoints,
      fileSizes,
      dependencyGraph,
      topLevelFolders,
      directoryColors,
      shortenFileName,
    );

    this.setState(
      {
        data: {
          graphData,
          entryPoints,
          fileSizes,
          dependencyGraph,
          topLevelFolders,
          directoryColors,
          reversedDependencyGraph,
        },
      },
      reFocus,
    );
  }

  addLink(link: NullableDepLink) {
    const { originalReversedDependencyGraph } = this.state;
    const { parent, child } = link;

    if (
      !parent ||
      !child ||
      !originalReversedDependencyGraph ||
      (!this.nodeExists(parent) && !originalReversedDependencyGraph[parent]) ||
      (!this.nodeExists(child) && !originalReversedDependencyGraph[child])
    ) {
      this.toast('Parent or Child does not exist');
      return;
    }
    if (parent === child) {
      this.toast('Cannot add a dependency to self');
      return;
    }

    const { addedLinks, removedLinks, focusedNodeId } = this.state;
    const linkEntry = JSON.stringify(link);
    if (removedLinks.has(linkEntry)) {
      removedLinks.delete(linkEntry);
    } else {
      addedLinks.add(linkEntry);
    }

    const originalId = focusedNodeId;
    this.focusNode('<total>');

    this.modifyDeps(addedLinks, removedLinks, () => {
      if (originalId && this.nodeExists(originalId)) {
        this.focusNode(originalId);
      }
    });
  }

  removeLink(link: NullableDepLink) {
    const { addedLinks, removedLinks, focusedNodeId } = this.state;
    const linkEntry = JSON.stringify(link);
    if (addedLinks.has(linkEntry)) {
      addedLinks.delete(linkEntry);
    } else {
      removedLinks.add(linkEntry);
    }

    const originalId = focusedNodeId;
    this.focusNode('<total>');

    this.modifyDeps(addedLinks, removedLinks, () => {
      if (originalId && this.nodeExists(originalId)) {
        this.focusNode(originalId);
      }
    });
  }

  nodeExists(id?: string) {
    const { data } = this.state;
    if (!data || !id) return false;

    const { entryPoints, reversedDependencyGraph } = data;

    if (id === '<total>' || id === '<unmapped>') {
      return true;
    }
    if (entryPoints.find((entryPoint) => id === entryPoint)) {
      return true;
    }
    const parents = reversedDependencyGraph[id];
    return parents && parents.length > 0;
  }

  focusNode(id?: string) {
    if (this.nodeExists(id)) {
      if (this.flameGraphRef.current) {
        this.flameGraphRef.current.focusNode(id);
      }
    } else {
      this.toast('Node does not exist');
    }
  }

  render() {
    const { url } = this.props;
    const {
      data,
      loadingMessage,
      loadingPercent,
      toastMessage,
      addedLinks,
      removedLinks,
      file,
      focusedNode,
      focusedNodeId,
    } = this.state;

    if (!url && !file) {
      return <Landing handleUpload={this.handleSetRawBundle} />;
    }

    if (!data) {
      return <Loader message={loadingMessage} percent={loadingPercent} />;
    }

    const {
      graphData,
      dependencyGraph,
      topLevelFolders,
      directoryColors,
      reversedDependencyGraph,
    } = data;

    const shortenFileName = getShortenFileName(topLevelFolders);

    return (
      <React.Fragment>
        <BundleFlameGraph
          flameGraphRef={this.flameGraphRef}
          data={graphData}
          onChange={(newFocusedNode, newFocusedNodeId) => {
            this.setState({
              focusedNode: newFocusedNode,
              focusedNodeId: newFocusedNodeId,
            });
          }}
        />
        <Footer
          focusedNode={focusedNode}
          focusedNodeId={focusedNodeId}
          directoryColors={directoryColors}
          topLevelFolders={topLevelFolders}
          dependencyGraph={dependencyGraph}
          reversedDependencyGraph={reversedDependencyGraph}
          removedLinks={removedLinks}
          addedLinks={addedLinks}
          removeLink={this.removeLink}
          addLink={this.addLink}
          focusNode={this.focusNode}
          shortenFileName={shortenFileName}
        />
        {toastMessage ? <Toast toastMessage={toastMessage} /> : null}
      </React.Fragment>
    );
  }
}

export default BundleFlameChart;
